import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { WhatsApp } from '@mui/icons-material'
import { ExplanationSection } from './ExplanationSection'
import { AppHeader } from './AppHeader'
import { AppFooter } from './AppFooter'

const WHATSAPP_NUMBER = '+12672145752'
// Prefill with the bootstrap command so the user's first send sets up the
// pdf-link flow (Fowix replies with the PDF + a reorder link).
const PREFILL = 'init pdf link'
const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(PREFILL)}`

export const SessionInput = () => {
  const [sessionId, setSessionId] = useState('')
  const navigate = useNavigate()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (sessionId.trim()) {
      navigate(`/view/${sessionId}`)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppHeader />
      <Box
        component="main"
        sx={{
          flex: 1,
          bgcolor: '#FAFBFC',
          backgroundImage: 'radial-gradient(#E2E8F0 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          py: { xs: 5, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Box sx={{ textAlign: 'center', maxWidth: 640 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: '#1E2A3B',
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  lineHeight: 1.15,
                  mb: 2,
                }}
              >
                Photos in. PDF out.
                <br />By WhatsApp.
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.125rem' } }}
              >
                Forward your photos to Fowix on WhatsApp, send "ok", and get back a
                PDF — with a link to reorder, rename, or email it.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'white',
                width: '100%',
                maxWidth: 480,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#1E2A3B', fontWeight: 600, mb: 1 }}>
                Start now
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Opens WhatsApp with "init pdf link" pre-filled. Hit send, then
                forward your photos and reply "ok" when you're done.
              </Typography>
              <Button
                component={MuiLink}
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                fullWidth
                startIcon={<WhatsApp />}
                sx={{
                  bgcolor: '#25D366',
                  color: 'white',
                  py: 1.5,
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1da851', boxShadow: 'none' },
                }}
              >
                Open WhatsApp
              </Button>
            </Paper>

            <ExplanationSection />

            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'white',
                width: '100%',
                maxWidth: 480,
              }}
            >
              <Typography variant="body2" sx={{ color: '#1E2A3B', fontWeight: 600, mb: 0.5 }}>
                Already have a session ID?
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                Paste it here to jump straight to your images.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. 84c6b86e-eeec-…"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={!sessionId.trim()}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Continue
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
      <AppFooter />
    </Box>
  )
}
